import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import "../../../styles/components/dialogs/accounts/addOrUpdateBusinessAccountsDialogComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import agentService from "../../../services/agents/agent.service";
import validationHelper from "../../../helpers/validation.helper";
import AccountErrorsEnum from "../../../enums/errors/accounts/accountErrors.enum";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import TextInputElementComponent from "../../general/elements/inputElements/TextInputElementComponent";
import useAgentListQuery from "../../../hooks/queries/useAgentListQuery";

// Creates or edits an Agent user. 0006 added identity fields (first name, last name, phone).
// Edit mode is entered when an `id` query param is present (?d=agent&action=add&id=...): the
// agent's current details are prefilled and the password field is hidden (use the reset action
// in the manage list to change a password). The token returned by create is intentionally
// ignored so the admin's own session is not replaced. Unlike the business dialog there is NO
// business-customer selection — agents are not tied to a customer.
const AddOrUpdateAgentDialogComponent = () => {
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("id");
  const isEditMode = !!agentId;

  const [credentials, setCredentials] = useState({ firstName: "", lastName: "", phoneNumber: "", email: "", password: "" });
  const [errors, setErrors] = useState({});

  // 0016: Edit mode prefill. There is no GetAgentById endpoint, so we find the agent in the shared
  // agent list — served from the React Query cache that ManageAgents already populated (no extra
  // network call; one deduped fetch). The query is only enabled in edit mode.
  const { data: agentList } = useAgentListQuery({ enabled: isEditMode });

  useEffect(() => {
    if (!isEditMode || !agentList) return;
    const agent = agentList.find((a) => a.id === agentId);
    if (!agent) {
      AlertService.error("הסוכן לא נמצא", "ייתכן שהסוכן נמחק");
      return;
    }
    setCredentials({
      firstName: agent.firstName || "",
      lastName: agent.lastName || "",
      phoneNumber: agent.phoneNumber || "",
      email: agent.email || "",
      password: "",
    });
  }, [isEditMode, agentId, agentList]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!validationHelper.validateText(credentials.firstName, true)) {
      newErrors.firstName = "יש להזין שם פרטי";
    }
    if (!validationHelper.validateText(credentials.lastName, true)) {
      newErrors.lastName = "יש להזין שם משפחה";
    }
    if (!validationHelper.validateEmail(credentials.email)) {
      newErrors.email = "כתובת דואר אלקטרוני לא תקינה";
    }
    // Password is only set at creation; in edit mode use the reset-password action.
    if (!isEditMode && !validationHelper.validatePassword(credentials.password)) {
      newErrors.password = "הסיסמה לא תקינה, על הסיסמה להיות לפחות 6 תווים";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRegisterErrorMessage = (errorCode) => {
    switch (errorCode) {
      case AccountErrorsEnum.EmailAlreadyExists:
        return "כתובת הדואר האלקטרוני כבר קיימת";
      default:
        return isEditMode ? "שגיאה בעדכון הסוכן" : "שגיאה ביצירת הסוכן";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      const response = isEditMode
        ? await agentService.updateAgent(agentId, {
            firstName: credentials.firstName,
            lastName: credentials.lastName,
            phoneNumber: credentials.phoneNumber,
            email: credentials.email,
          })
        : await agentService.registerAgent(credentials);

      if (!validationHelper.validateHttpResponse(response.data)) {
        setErrors({ general: getRegisterErrorMessage(response.data?.error?.code) });
        return;
      }

      AlertService.success(isEditMode ? "פרטי הסוכן עודכנו בהצלחה" : "הסוכן נוצר בהצלחה");
      if (!isEditMode) {
        setCredentials({ firstName: "", lastName: "", phoneNumber: "", email: "", password: "" });
      }
    } catch (error) {
      setErrors({ general: getRegisterErrorMessage(error.response?.data?.error?.code) });
      console.error("Error saving agent:", error);
    }
  };

  const getTextInputElement = (name, type, placeholder) => {
    const inputProps = {
      ...textInputModel,
      name,
      type,
      error: errors[name],
      value: credentials[name],
      placeholder,
      onChange: handleInputChange,
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  return (
    <div className="dialogContainer addOrUpdateBusinessAccountsDialogContainer">
      <h2>{isEditMode ? "עריכת סוכן" : "יצירת סוכן חדש"}</h2>
      <form onSubmit={handleSubmit}>
        {getTextInputElement("firstName", "text", "הכנס שם פרטי")}
        {getTextInputElement("lastName", "text", "הכנס שם משפחה")}
        {getTextInputElement("phoneNumber", "text", "הכנס מספר טלפון")}
        {getTextInputElement("email", "email", "הכנס דואר אלקטרוני")}
        {!isEditMode && getTextInputElement("password", "password", "הכנס סיסמה")}

        <button type="submit" className="primaryButton">
          שמור
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
      <Link to="?d=agent&action=manage" className="backToListLink">
        חזרה לרשימת הסוכנים
      </Link>
    </div>
  );
};

export default AddOrUpdateAgentDialogComponent;
